/**
 * Picks run times that don't collide with an existing crawl of the same brand,
 * and that finish before the dealership opens.
 *
 * Two real problems motivate this. First, crawling the same OEM from two
 * pipelines at once invites rate-limiting and blocks — and it already happens:
 * one Ford schedule runs 05:00 America/New_York while another runs 04:00
 * America/Chicago, which is the *same instant* despite reading an hour apart.
 * Second, a refresh is only useful if it has finished by the time staff arrive.
 *
 * Everything here compares real UTC instants rather than wall-clock numbers.
 * That is the whole reason the collision above is invisible today.
 */
import type { PipelineSchedule } from '../services/pipelineOrchestrator';
import { getNextRun } from './cronNextRun';
import { parseCron, matchingValues, FIELD_RANGE } from './cronFields';

/**
 * How long a run is assumed to take when deciding whether it finishes before
 * opening. The p90 of 60 observed runs is 106 minutes (median 61), so two hours
 * is the next round number above it. Deliberately pessimistic: overshooting
 * only costs a slightly earlier suggestion, while undershooting means stale
 * data on the screen when the service desk opens.
 */
export const ASSUMED_RUN_MINUTES = 120;

/**
 * Minimum separation between two runs covering the same OEM. Matches the
 * `min_interval_minutes` already configured on every schedule.
 */
export const SAME_OEM_GAP_MINUTES = 60;

/** Dealer-local hours when nobody is working. Runs should land inside this. */
export const OFF_HOURS_START = 18; // 6 PM
export const OFF_HOURS_END = 6; // 6 AM — also when business hours begin

const MINUTES_PER_DAY = 24 * 60;

export interface SlotContext {
  /** Every schedule, any tenant — collisions are cross-tenant by nature. */
  existing: PipelineSchedule[];
  /** OEM prefixes the new schedule will crawl. */
  oems: string[];
  /** Days the new schedule runs, 0 = Sunday. */
  daysOfWeek: number[];
  /** Timezone the chosen time is expressed in (what gets saved). */
  scheduleZone: string;
  /** The dealership's own timezone, used only to judge off-hours. */
  dealerZone: string;
}

export interface Slot {
  hour: number;
  minute: number;
  /** Starts inside the dealer's closed window. */
  offHours: boolean;
  /** Start + ASSUMED_RUN_MINUTES still lands before the dealer opens. */
  finishesBeforeOpen: boolean;
  /** The OEM of a conflicting schedule, or null when the slot is clear. */
  conflictingOem: string | null;
  /** Minutes to the nearest same-OEM run; Infinity when there is none. */
  nearestOemGapMinutes: number;
  /** Higher is better. Negative means unusable. */
  score: number;
}

/** `ford-tampa-bay` → `ford`. The prefix is the only OEM identifier a saved schedule carries. */
export function oemOf(zoneSlug: string): string {
  const [prefix = ''] = zoneSlug.split('-');
  return prefix.toLowerCase();
}

/** Distinct OEM prefixes a schedule crawls. */
export function scheduleOems(schedule: PipelineSchedule): string[] {
  const out = new Set<string>();
  for (const zone of schedule.pipeline_config.zones ?? []) {
    const oem = oemOf(zone.zone_slug);
    if (oem) out.add(oem);
  }
  return [...out];
}

/** Days a schedule runs, read straight off its cron. Empty when unreadable. */
function scheduleDays(schedule: PipelineSchedule): number[] {
  const parts = parseCron(schedule.cron_expression);
  if (!parts) return [];
  return matchingValues(parts.dayOfWeek, FIELD_RANGE.dayOfWeek.min, FIELD_RANGE.dayOfWeek.max);
}

/**
 * Where a schedule's run falls in the day, as minutes past midnight *in the
 * comparison zone*.
 *
 * Derived from a real next-run instant rather than the cron's hour field, so a
 * schedule stored in Eastern and one stored in Central are placed on the same
 * axis. Returns null when the cron can't be read.
 */
function minuteOfDayIn(schedule: PipelineSchedule, zone: string, from: Date): number | null {
  const run = getNextRun(schedule.cron_expression, schedule.time_zone, from);
  if (!run) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(run.instant);

  const hour = Number(parts.find(p => p.type === 'hour')?.value);
  const minute = Number(parts.find(p => p.type === 'minute')?.value);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  // Intl renders midnight as 24 in some locales/engines.
  return (hour % 24) * 60 + minute;
}

/** Shortest distance between two times of day, wrapping across midnight. */
function circularGap(a: number, b: number): number {
  const raw = Math.abs(a - b);
  return Math.min(raw, MINUTES_PER_DAY - raw);
}

interface ExistingRun {
  oem: string;
  minuteOfDay: number;
  days: Set<number>;
  scheduleName: string;
}

/**
 * Flattens the existing schedules into one entry per (schedule, OEM), placed on
 * the comparison zone's clock. Disabled schedules are skipped — a paused
 * schedule isn't competing for bandwidth.
 */
function existingRuns(ctx: SlotContext, from: Date): ExistingRun[] {
  const out: ExistingRun[] = [];

  for (const schedule of ctx.existing) {
    if (!schedule.is_enabled) continue;

    const minuteOfDay = minuteOfDayIn(schedule, ctx.scheduleZone, from);
    if (minuteOfDay === null) continue;

    const days = new Set(scheduleDays(schedule));
    for (const oem of scheduleOems(schedule)) {
      out.push({ oem, minuteOfDay, days, scheduleName: schedule.schedule_name });
    }
  }

  return out;
}

/** Minutes past midnight, dealer-local, for a time expressed in the schedule zone. */
function toDealerMinuteOfDay(minuteOfDay: number, ctx: SlotContext, from: Date): number {
  const offset =
    zoneOffsetMinutes(ctx.dealerZone, from) - zoneOffsetMinutes(ctx.scheduleZone, from);
  return (((minuteOfDay + offset) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/** A zone's UTC offset in minutes at a given instant. */
function zoneOffsetMinutes(zone: string, at: Date): number {
  try {
    // 'shortOffset' renders e.g. "GMT-5" / "GMT-4:30"; parse rather than guess.
    const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(at)
      .find(p => p.type === 'timeZoneName')?.value;
    const match = name?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0));
  } catch {
    return 0;
  }
}

/** True when a dealer-local minute-of-day sits inside the closed window. */
function isOffHours(dealerMinute: number): boolean {
  const hour = Math.floor(dealerMinute / 60);
  return hour >= OFF_HOURS_START || hour < OFF_HOURS_END;
}

/** Minutes from a dealer-local time until the dealer opens. */
function minutesUntilOpen(dealerMinute: number): number {
  const open = OFF_HOURS_END * 60;
  return dealerMinute < open ? open - dealerMinute : MINUTES_PER_DAY - dealerMinute + open;
}

/**
 * The closest existing run of a brand this schedule also crawls.
 *
 * Only schedules sharing a day-of-week count: two Ford crawls at the same hour
 * on Saturday and Sunday never actually run together.
 */
function findOemConflict(
  minuteOfDay: number,
  ctx: SlotContext,
  from: Date
): { conflictingOem: string | null; nearestOemGapMinutes: number } {
  const wanted = new Set(ctx.oems);
  let conflictingOem: string | null = null;
  let nearestOemGapMinutes = Infinity;

  for (const run of existingRuns(ctx, from)) {
    const overlapsDay = run.days.size === 0 || ctx.daysOfWeek.some(d => run.days.has(d));
    if (!wanted.has(run.oem) || !overlapsDay) continue;

    const gap = circularGap(minuteOfDay, run.minuteOfDay);
    if (gap < nearestOemGapMinutes) {
      nearestOemGapMinutes = gap;
      if (gap < SAME_OEM_GAP_MINUTES) conflictingOem = run.oem;
    }
  }

  return { conflictingOem, nearestOemGapMinutes };
}

/**
 * Evaluates one candidate time.
 *
 * Scoring encodes "fresh data when the doors open": among slots that finish in
 * time, the *latest* start wins, because a run finishing at 5:45 AM leaves less
 * stale data than one finishing at 1 AM. Evening slots are usable but rank
 * below every overnight one — by opening, their data is twelve hours old.
 */
export function evaluateSlot(
  hour: number,
  minute: number,
  ctx: SlotContext,
  from: Date = new Date()
): Slot {
  const minuteOfDay = hour * 60 + minute;
  const dealerMinute = toDealerMinuteOfDay(minuteOfDay, ctx, from);
  const offHours = isOffHours(dealerMinute);
  const untilOpen = minutesUntilOpen(dealerMinute);
  // Guarded by offHours on purpose. A start already inside business hours
  // wraps to *tomorrow's* opening, so the raw arithmetic would report a
  // comfortable 22-hour margin for a run the staff are sitting through.
  const finishesBeforeOpen = offHours && untilOpen >= ASSUMED_RUN_MINUTES;

  const { conflictingOem, nearestOemGapMinutes } = findOemConflict(minuteOfDay, ctx, from);

  let score: number;
  if (conflictingOem || !offHours) {
    score = -1;
  } else if (finishesBeforeOpen) {
    // Closer to opening is fresher. untilOpen is smallest for the latest
    // qualifying start, so invert it.
    score = 1000 - untilOpen;
  } else {
    // Off-hours but won't finish in time — an evening slot. Still usable,
    // ranked below everything overnight.
    score = 100 - untilOpen / 60;
  }

  return {
    hour,
    minute,
    offHours,
    finishesBeforeOpen,
    conflictingOem,
    nearestOemGapMinutes,
    score
  };
}

/**
 * Every half-hour candidate, best first, unusable ones dropped.
 *
 * Half-hourly rather than hourly because the existing schedules already use
 * :30 slots (02:30, 03:30, 04:30, 05:30) and excluding them would discard
 * usable gaps on a crowded OEM like Ford.
 */
export function rankSlots(ctx: SlotContext, from: Date = new Date()): Slot[] {
  const slots: Slot[] = [];
  for (let minuteOfDay = 0; minuteOfDay < MINUTES_PER_DAY; minuteOfDay += 30) {
    slots.push(evaluateSlot(Math.floor(minuteOfDay / 60), minuteOfDay % 60, ctx, from));
  }
  return slots.filter(s => s.score >= 0).sort((a, b) => b.score - a.score);
}

/** Formats a slot as a 12-hour label, e.g. `3:30 AM`. */
export function slotLabel(slot: Pick<Slot, 'hour' | 'minute'>): string {
  const period = slot.hour >= 12 ? 'PM' : 'AM';
  const h12 = slot.hour % 12 || 12;
  return `${h12}:${String(slot.minute).padStart(2, '0')} ${period}`;
}
